import { useState } from 'react';
import { generateCompliantPassword, passwordMeetsAllRules } from '@physioedge/shared';
import { api, ApiError } from '../api';
import { Alert, Button, Input, PasswordChecklist, PasswordInput } from './ui';

export type BranchStaffMember = {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  isPrimary: boolean;
  password?: string | null;
  locationIds: string[];
};

export type BranchLocationOption = {
  id: string;
  name: string;
};

function looksLikeError(message: string) {
  return /failed|could not|cannot |must |required|invalid|enter a |pick a /i.test(message);
}

/**
 * Assign branch-staff accounts to clinic locations. Used by PhysioEDGE admin
 * and by the clinic's main account — same checkboxes and add-staff form.
 */
export function BranchStaffManager({
  locations,
  staffMembers,
  onCreate,
  onAssignBranches,
  className = '',
}: {
  locations: BranchLocationOption[];
  staffMembers: BranchStaffMember[];
  onCreate: (input: {
    email: string;
    fullName: string;
    phone: string;
    password: string;
    locationIds: string[];
  }) => Promise<{ email: string; password?: string; temporaryPassword?: string; credentialsEmailed: boolean }>;
  onAssignBranches: (staffId: string, locationIds: string[]) => Promise<void>;
  className?: string;
}) {
  const [form, setForm] = useState({ email: '', fullName: '', phone: '', password: '', locationIds: [] as string[] });
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');
  const branchStaff = staffMembers.filter(s => !s.isPrimary);

  const branchLabel = (loc: BranchLocationOption) => loc.name.trim() || 'Branch';

  const toggleLoc = (id: string) => {
    setForm(f => ({
      ...f,
      locationIds: f.locationIds.includes(id)
        ? f.locationIds.filter(x => x !== id)
        : [...f.locationIds, id],
    }));
  };

  const create = async () => {
    setCreating(true);
    setMsg('');
    try {
      const password = form.password.trim() || generateCompliantPassword();
      if (!passwordMeetsAllRules(password)) {
        setMsg('Password must meet all rules, or click Generate.');
        setCreating(false);
        return;
      }
      const res = await onCreate({
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        password,
        locationIds: form.locationIds,
      });
      setForm({ email: '', fullName: '', phone: '', password: '', locationIds: [] });
      setAdding(false);
      setMsg(`Staff added. Email: ${res.email} · Password: ${res.password ?? res.temporaryPassword ?? password}`);
    } catch (err) {
      setMsg(err instanceof ApiError ? err.friendlyMessage : 'Failed.');
    } finally {
      setCreating(false);
    }
  };

  const saveBranches = async (staffId: string, locationIds: string[]) => {
    try {
      await onAssignBranches(staffId, locationIds);
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-ink">Branch staff</div>
        <Button
          type="button"
          size="sm"
          variant={adding ? 'ghost' : 'secondary'}
          aria-expanded={adding}
          onClick={() => {
            setAdding(v => !v);
            setMsg('');
          }}
        >
          {adding ? 'Cancel' : '+ Add staff'}
        </Button>
      </div>
      {branchStaff.length === 0 && !adding && <p className="text-xs text-ink-soft">No branch staff yet.</p>}
      {branchStaff.map(s => (
        <div key={s.id} className="rounded-radius-sm border border-line p-3 space-y-2">
          <div className="text-sm font-medium text-ink">
            {s.fullName}
          </div>
          <div className="text-xs text-ink-soft space-y-0.5">
            <p>Email: <span className="text-ink">{s.email}</span></p>
            <p>Password: <span className="text-ink">{s.password || 'Not set yet'}</span></p>
          </div>
          {locations.length === 0 ? (
            <p className="text-xs text-ink-soft">Add branches first, then assign them here.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {locations.map(loc => {
                const checked = (s.locationIds ?? []).includes(loc.id);
                return (
                  <label key={loc.id} className="text-xs flex items-center gap-1.5 text-ink">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? s.locationIds.filter(id => id !== loc.id)
                          : [...s.locationIds, loc.id];
                        if (next.length === 0) return;
                        void saveBranches(s.id, next);
                      }}
                    />
                    {branchLabel(loc)}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ))}
      {adding && (
        <div className="space-y-3 rounded-radius-sm border border-line p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Full name" value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} />
            <Input label="Login email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            <Input label="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <PasswordInput
                  label="Password"
                  value={form.password}
                  autoComplete="new-password"
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                />
              </div>
              <Button type="button" size="sm" variant="secondary" onClick={() => setForm(p => ({ ...p, password: generateCompliantPassword() }))}>
                Generate
              </Button>
            </div>
            <PasswordChecklist password={form.password} />
          </div>
          {locations.length > 0 && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-ink-soft">Branches they can manage</legend>
              <div className="flex flex-wrap gap-2">
                {locations.map(loc => (
                  <label key={loc.id} className="text-xs flex items-center gap-1.5 text-ink">
                    <input type="checkbox" checked={form.locationIds.includes(loc.id)} onChange={() => toggleLoc(loc.id)} />
                    {branchLabel(loc)}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <Button
            size="sm"
            loading={creating}
            disabled={!form.email || !form.fullName || !form.phone || form.locationIds.length === 0}
            onClick={() => { void create(); }}
          >
            Save staff
          </Button>
        </div>
      )}
      {msg ? (
        <Alert type={looksLikeError(msg) ? 'error' : 'success'}>{msg}</Alert>
      ) : null}
    </div>
  );
}

/** Admin wrapper around the shared branch-staff manager. */
export function AdminBranchStaffManager({
  clinicId,
  locations,
  staffMembers,
  onChanged,
}: {
  clinicId: string;
  locations: BranchLocationOption[];
  staffMembers: BranchStaffMember[];
  onChanged: () => void;
}) {
  return (
    <BranchStaffManager
      className="border-t border-line pt-4"
      locations={locations}
      staffMembers={staffMembers}
      onCreate={async input => {
        const res = await api.post<{
          staff: { email: string };
          password?: string;
          temporaryPassword?: string;
          credentialsEmailed: boolean;
        }>(`/api/admin/clinics/${clinicId}/staff`, input);
        onChanged();
        return {
          email: res.staff.email,
          password: res.password ?? res.temporaryPassword,
          temporaryPassword: res.temporaryPassword,
          credentialsEmailed: res.credentialsEmailed,
        };
      }}
      onAssignBranches={async (staffId, locationIds) => {
        await api.patch(`/api/admin/clinics/${clinicId}/staff/${staffId}`, { locationIds });
        onChanged();
      }}
    />
  );
}
